import { useQuery } from '@tanstack/react-query'

export interface GeoRow { id: string; name: string }

/* Zambian province/district reference data, served by public_bp
 * (url_prefix "/public") as GET /public/api/provinces and
 * GET /public/api/districts?province_id=<id> - each row {id, name}. This is
 * unauthenticated reference data on a different mount than the Bearer/cookie
 * -authenticated /api/v1 surface lib/api.ts's `api` helper targets, so these
 * fetch the relative path directly instead of going through it.
 *
 * Firebase Hosting rewrites /public/** to the same Cloud Run service in
 * production (web/firebase.json), so a same-origin relative fetch resolves
 * there without any extra configuration. The local Vite dev server needs a
 * matching proxy entry for the same path (see vite.config.ts) or these calls
 * 404 against the dev server instead of reaching Flask. */
async function fetchPublicJson<T>(path: string): Promise<T> {
  const res = await fetch(path)
  if (!res.ok) throw new Error(`Request failed (${res.status})`)
  return res.json() as Promise<T>
}

/** Zambia's provinces. This codebase's UI/route param calls the same value
 * `region_id` - "province" is just the human label. */
export function useProvinces(): GeoRow[] {
  const { data } = useQuery({
    queryKey: ['public', 'provinces'],
    queryFn: () => fetchPublicJson<{ provinces: GeoRow[] }>('/public/api/provinces'),
    staleTime: Infinity, // reference data - changes essentially never
  })
  return data?.provinces ?? []
}

/** Districts for a given province/region id. Disabled (and empty) until a
 * province is actually selected. */
export function useDistricts(provinceId: string): GeoRow[] {
  const { data } = useQuery({
    queryKey: ['public', 'districts', provinceId],
    queryFn: () => fetchPublicJson<{ districts: GeoRow[] }>(
      `/public/api/districts?province_id=${encodeURIComponent(provinceId)}`,
    ),
    enabled: Boolean(provinceId),
    staleTime: Infinity,
  })
  return data?.districts ?? []
}
